import React,{useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiUser,FiMail,FiPhone,FiBookOpen,FiMessageSquare,FiSend,FiCheckCircle}=FiIcons;

const BookTutor=()=> {
  const [formData,setFormData]=useState({
    fullName: '',
    email: '',
    whatsapp: '',
    service: '',
    message: ''
  });

  const [isSubmitting,setIsSubmitting]=useState(false);

  const handleChange=(e)=> {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email via Cloudflare Pages Function
      const response = await fetch('/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formType: 'book-tutor',
          formData: formData
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          whatsapp: '',
          service: '',
          message: ''
        });
        
        alert('✅ Your booking request has been sent successfully! We\'ll contact you within 24 hours. Check your email for confirmation.');
      } else {
        throw new Error(result.error || 'Failed to send request');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('⚠️ There was an error sending your request. Please try again or contact us at info@tutorhubonline.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services=[
    'Business Management',
    'Data Analysis',
    'Online Exam Help US'
  ];

  const containerVariants={
    hidden: {opacity: 0},
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants={
    hidden: {y: 20,opacity: 0},
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-blue-600 to-blue-800 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-blue-600/80 to-blue-800/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0,y: 30}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Book an Online Tutor
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 max-w-4xl mx-auto mb-8">
              Get personalized academic support from expert tutors. Fill out the form below and we'll connect you with the perfect tutor for your needs via WhatsApp.
            </p>
            <div className="inline-flex items-center bg-accent/20 backdrop-blur-sm text-accent px-6 py-3 rounded-full border border-accent/30">
              <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mr-2" />
              <span className="font-semibold">Instant WhatsApp Response</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Booking Form Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{once: true,margin: "-100px"}}
        variants={containerVariants}
        className="py-20"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Book Your Tutor Now</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Fill out the form below and we'll contact you via WhatsApp within minutes to match you with the perfect tutor.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-xl p-8 border">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <motion.div
                initial={{opacity: 0,x: -20}}
                whileInView={{opacity: 1,x: 0}}
                transition={{duration: 0.5}}
              >
                <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiUser} className="inline w-4 h-4 mr-2 text-primary" />
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  required
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                  placeholder="Enter your full name"
                />
              </motion.div>

              {/* Email Address */}
              <motion.div
                initial={{opacity: 0,x: -20}}
                whileInView={{opacity: 1,x: 0}}
                transition={{duration: 0.5,delay: 0.1}}
              >
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiMail} className="inline w-4 h-4 mr-2 text-primary" />
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                  placeholder="your.email@example.com"
                />
              </motion.div>

              {/* WhatsApp Number */}
              <motion.div
                initial={{opacity: 0,x: -20}}
                whileInView={{opacity: 1,x: 0}}
                transition={{duration: 0.5,delay: 0.2}}
              >
                <label htmlFor="whatsapp" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiPhone} className="inline w-4 h-4 mr-2 text-primary" />
                  WhatsApp Number *
                </label>
                <input
                  type="tel"
                  id="whatsapp"
                  name="whatsapp"
                  required
                  value={formData.whatsapp}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                  placeholder="+44 7440 373308"
                />
              </motion.div>

              {/* Service Needed */}
              <motion.div
                initial={{opacity: 0,x: -20}}
                whileInView={{opacity: 1,x: 0}}
                transition={{duration: 0.5,delay: 0.3}}
              >
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiBookOpen} className="inline w-4 h-4 mr-2 text-primary" />
                  Tutor / Service Needed *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                >
                  <option value="">Select a service...</option>
                  {services.map((service,index)=> (
                    <option key={index} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </motion.div>

              {/* Message */}
              <motion.div
                initial={{opacity: 0,x: -20}}
                whileInView={{opacity: 1,x: 0}}
                transition={{duration: 0.5,delay: 0.4}}
              >
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiMessageSquare} className="inline w-4 h-4 mr-2 text-primary" />
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 resize-none"
                  placeholder="Please describe your specific needs,subjects,deadlines,or any other requirements..."
                ></textarea>
              </motion.div>

              {/* Submit Button */}
              <motion.div
                initial={{opacity: 0,y: 20}}
                whileInView={{opacity: 1,y: 0}}
                transition={{duration: 0.5,delay: 0.5}}
                className="text-center pt-4"
              >
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  className={`w-full sm:w-auto bg-primary text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center justify-center text-lg ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </motion.button>
              </motion.div>
            </form>
          </motion.div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{once: true,margin: "-100px"}}
        variants={containerVariants}
        className="py-16 bg-primary text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-blue-600 to-blue-800" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 variants={itemVariants} className="text-3xl lg:text-4xl font-bold mb-4">
            🎯 Ready to Transform Your Academic Journey?
          </motion.h2>
          <motion.p variants={itemVariants} className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join thousands of students who have improved their grades and confidence with our expert tutoring services. Get started today!
          </motion.p>
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="https://wa.me/447440373308"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              className="bg-accent text-black px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
            >
              💬 WhatsApp: +44 7440 373308
            </motion.a>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default BookTutor;